import axios from "axios";
import { useContext, useEffect, useState } from "react";
import emailjs from 'emailjs-com';
import { AccessibilityContext } from "../../../contexts/AccessibilityContext";
import facebooklogo from "../../../assets/icons/facebooklogo.png";
import { useTranslation } from "react-i18next";

interface FormData {
    [key: string]: string | boolean;
}

interface FormErrors {
    [key: string]: boolean;
}

interface Task {
    question: string;
    correctAnswer: string;
    userAnswer: string;
}

export const ContactFormSection = () => {
    const { isContrast } = useContext(AccessibilityContext);
    const [isSent, setIsSent] = useState<boolean>(false);
    const [task, setTask] = useState<Task | null>(null);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        subject: '',
        message: '',
        consent: false
    });
    const fbBg = isContrast
        ? "bg-gradient-to-r from-yellow-300 to-yellow-500"
        : "bg-gradient-to-r from-[#1877F2] to-[#3b5998] hover:from-[#3b5998] hover:to-[#1877F2]";
    const liBg = isContrast
        ? "bg-gradient-to-r from-yellow-300 to-yellow-500"
        : "bg-gradient-to-r from-[#0A66C2] to-[#0e76a8] hover:from-[#0e76a8] hover:to-[#0A66C2]";
    const iconFill = isContrast ? "#000000" : "#FFFFFF";
    const [formErrors, setFormErrors] = useState<FormErrors>({
        name: false,
        email: false,
        subject: false,
        message: false,
        consent: false
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        setFormData({ ...formData, [target.name]: value });

        if (target.type === 'checkbox') {
            setFormErrors({ ...formErrors, [target.name]: !target.checked });
        } else {
            setFormErrors({ ...formErrors, [target.name]: target.value.trim() === '' });
        }
    };

    const handleTaskAnswerChange = (value: string) => {
        if (task) {
            setTask({ ...task, userAnswer: value });
        }
    };

    const validateForm = () => {
        const errors: FormErrors = {};
        Object.keys(formData).forEach(key => {
            const value = formData[key];
            if (typeof value === 'string' && value.trim() === '') {
                errors[key] = true;
            } else if (typeof value === 'boolean' && !value) {
                errors[key] = true;
            }
        });

        if (task && task.userAnswer !== task.correctAnswer) {
            errors['task'] = true;
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const generateTasks = (): Task[] => [
        generateAdditionTask(),
        generateSubtractionTask(),
        generateMultiplicationTask(),
    ];

    const selectRandomTask = () => {
        const tasks = generateTasks();
        const randomTask = tasks[Math.floor(Math.random() * tasks.length)];
        setTask(randomTask);
    };

    useEffect(() => {
        selectRandomTask();
    }, []);

    const { t, i18n } = useTranslation();

    const sendEmail = (e: any) => {
        e.preventDefault();
        if (!validateForm()) {
            alert(t("Proszę poprawnie rozwiązać zadanie i wypełnić wszystkie wymagane pola."));
            return;
        }
        setIsSent(true);
        emailjs.send('service_d0w2urj', 'template_64jjx4n', formData, 'CsXyVwhmgAZQ_AJqT')
            .then((result: any) => {
                alert(t("Wiadomość została wysłana!"));
            }, (error: any) => {
                setIsSent(false);
                alert(t("Wystąpił błąd przy wysyłaniu wiadomości."));
            });
    };

    return (
        <section className="md:w-[900px] flex flex-col gap-20" id="kontakt">
            <div className="mx-auto w-full justify-center">
                <h2 className={(isContrast ? "text-yellow-400" : " text-black ") + " text-custom-40px font-bold leading-[47.41px] text-center pb-[78px] mt-[40px]"}>
                    {t("Chcesz wiedzieć więcej?")}
                </h2>
                <div className={(isContrast ? "bg-cyan-950" : " bg-white") + " md:px-[30px] px-[20px] py-10 rounded-xl justify-between items-start gap-12 flex md:flex-row flex-col "}
                    style={{ boxShadow: '0px 0px 24.399999618530273px 6px rgba(0, 0, 0, 0.10)' }}>
                    <div className="md:w-[354px] w-[300px] flex flex-col md:h-[640px]">
                        <span className={(isContrast ? "text-yellow-400" : " text-black") + " md:w-[346px] mb-[20px] text-custom-extended-40px  font-bold leading-[47.41px] md:text-left text-center"}>
                            {t("Zadaj nam pytanie")}
                        </span>
                        <div className="flex flex-col h-full justify-between">
                            <div className="">
                                <span className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-xl font-normal"}>
                                    {t("Chcesz, aby Twoja placówka była bardziej dostępna dla osób niewidomych i niedowidzących?")} <br />
                                </span>
                                {i18n.language == "pl" ?
                                    <>
                                        <span className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-xl font-normal"}>
                                            W sekcji
                                        </span>
                                        <a href="#faq" className={(isContrast ? "text-red-600" : "  text-cyan-700") + " text-custom-xl font-normal underline px-1"}>
                                            FAQ
                                        </a>
                                        <span className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-xl font-normal"}>
                                            nie znalazłeś odpowiedzi na Twoje pytania<br />
                                        </span>
                                    </>
                                    :
                                    <>
                                        <span className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-xl font-normal"}>
                                            Didn't find the answer to your questions in the
                                        </span>
                                        <a href="#faq" className={(isContrast ? "text-red-600" : "  text-cyan-700") + " text-custom-xl font-normal underline px-1"}>
                                            FAQ
                                        </a>
                                        <span className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-xl font-normal"}>
                                            section?<br />
                                        </span>
                                    </>
                                }
                                <div className="md:text-left text-center">
                                    <span className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-xl font-bold"}>
                                        {t("Napisz do nas!")}
                                    </span>
                                </div>
                            </div>
                            <div className="md:flex md:flex-col flex-row md:gap-0 gap-4 hidden">
                                <div className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-19px font-semibold leading-[27px]"}>
                                    {t("Zobacz jak działamy na: ")}
                                </div>
                                <div className="flex items-center gap-4">
                                    <a
                                        href="https://www.facebook.com/profile.php?id=100064351974958"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Facebook TOTUPOINT"
                                        title="Facebook"
                                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 ${fbBg}`}
                                    >
                                        <svg className="w-6 h-6" role="img" aria-hidden="true" viewBox="0 0 24 24">
                                            <path
                                                d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.35C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.732 0 1.325-.593 1.325-1.325V1.325C24 .593 23.407 0 22.675 0z"
                                                fill={iconFill}
                                            />
                                        </svg>
                                    </a>
                                    <a
                                        href="https://www.linkedin.com/company/totupoint/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="LinkedIn TOTUPOINT"
                                        title="LinkedIn"
                                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 ${liBg}`}
                                    >
                                        <svg className="w-6 h-6" role="img" aria-hidden="true" viewBox="0 0 24 24">
                                            <path
                                                d="M20.447 20.452h-3.554v-5.569c0-1.328-.026-3.037-1.852-3.037-1.853 0-2.136 1.447-2.136 2.943v5.663H9.351V9h3.414v1.561h.048c.476-.9 1.64-1.852 3.374-1.852 3.608 0 4.274 2.376 4.274 5.466v6.277zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.114 20.452H3.557V9h3.557v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.73v20.541C0 23.226.792 24 1.771 24h20.454C23.2 24 24 23.226 24 22.271V1.73C24 .774 23.2 0 22.225 0z"
                                                fill={iconFill}
                                            />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {!isSent ?
                        <form className="w-full flex flex-col md:gap-[23px] gap-[10px]" onSubmit={sendEmail} noValidate>
                            <div className="">
                                <label htmlFor="name" className="sr-only">{t("Imię")} *</label>
                                <input id="name" className={` p-4 w-full h-[55px] bg-white rounded-lg border ${formErrors.name ? 'border-red-500' : 'border-cyan-700 border-opacity-70'}`} type="text" name="name" onChange={handleChange} placeholder={t("Imię") + " *"} required aria-invalid={formErrors.name} aria-describedby="error-name" />
                                {formErrors.name && <span id="error-name" className="text-red-500"> {t("To pole jest wymagane.")}</span>}
                            </div>
                            <div className="">
                                <label htmlFor="email" className="sr-only">{t("Email")} *</label>
                                <input id="email" className={` p-4 w-full h-[55px] bg-white rounded-lg border ${formErrors.email ? 'border-red-500' : 'border-cyan-700 border-opacity-70'}`} type="email" name="email" onChange={handleChange} placeholder={t("Email") + " *"} required aria-invalid={formErrors.email} aria-describedby="error-email" />
                                {formErrors.email && <span id="error-email" className="text-red-500"> {t("To pole jest wymagane.")}</span>}
                            </div>
                            <div className="">
                                <label htmlFor="subject" className="sr-only">{t("Temat")} *</label>
                                <input id="subject" className={` p-4 w-full h-[55px] bg-white rounded-lg border ${formErrors.subject ? 'border-red-500' : 'border-cyan-700 border-opacity-70'}`} type="text" name="subject" onChange={handleChange} placeholder={t("Temat") + " *"} required aria-invalid={formErrors.subject} aria-describedby="error-subject" />
                                {formErrors.subject && <span id="error-subject" className="text-red-500"> {t("To pole jest wymagane.")}</span>}
                            </div>
                            <div className="">
                                <label htmlFor="message" className="sr-only">{t("Wiadomość")} *</label>
                                <textarea id="message" className={` p-4 w-full h-[140px] bg-white rounded-lg border ${formErrors.message ? 'border-red-500' : 'border-cyan-700 border-opacity-70'}`} name="message" onChange={handleChange} placeholder={t("Napisz do nas wiadomość") + " *"} required aria-invalid={formErrors.message} aria-describedby="error-message"></textarea>
                                {formErrors.message && <span id="error-message" className="text-red-500"> {t("To pole jest wymagane.")}</span>}
                            </div>
                            <div className="">
                                <input type="checkbox" id="consent" name="consent" checked={Boolean(formData.consent)} onChange={handleChange} required aria-describedby="error-consent" />
                                <label htmlFor="consent" className={(isContrast ? "text-yellow-400" : "text-black") + " ml-2 text-custom-xl font-normal"}>
                                    {t("Wyrażam zgodę na przetwarzanie moich danych osobowych w celu odpowiedzi na przesłaną wiadomość zgodnie z polityką prywatności.")}
                                </label>
                                <div className="flex flex-col">
                                    {formErrors.consent && <span id="error-consent" className="text-red-500"> {t("To pole jest wymagane.")}</span>}
                                </div>
                            </div>
                            <div className="flex flex-col gap-4">
                                {task && (
                                    <div>
                                        <label>
                                            {t("Rozwiąż zadanie, aby udowodnić, że jesteś człowiekiem")}: {task.question}
                                            <input
                                                type="text"
                                                value={task.userAnswer}
                                                onChange={(e) => handleTaskAnswerChange(e.target.value)}
                                                className={`p-4 w-full h-[55px] bg-white rounded-lg border ${formErrors['task'] ? 'border-red-500' : 'border-cyan-700 border-opacity-70'}`}
                                                required
                                            />
                                        </label>
                                        {formErrors['task'] && <span className="text-red-500">{t("Please solve the task correctly.")}</span>}
                                    </div>
                                )}
                            </div>
                            <button type="submit" className="h-[43px] px-4 py-2.5 bg-cyan-700 rounded-lg justify-center items-center text-white text-custom-xl font-medium cursor-pointer hover:bg-cyan-800">
                                {t("Wyślij")}
                            </button>
                        </form>
                        :
                        <div className=" h-[43px] md:px-[50px] w-full py-2.5 bg-gray-800 rounded-lg justify-center items-center gap-2.5 inline-flex">
                            <div className="text-white text-custom-xl font-medium">{t("Wysłano")}</div>
                        </div>
                    }
                    <div className="flex md:flex-col flex-row md:gap-0 gap-4 md:hidden">
                        <div className={(isContrast ? "text-yellow-400" : " text-black") + " text-custom-19px font-semibold leading-[27px] pt-1"}>
                            {t("Zobacz jak działamy na:")}
                        </div>
                        <div className="flex items-center gap-4">
                            <a
                                href="https://www.facebook.com/profile.php?id=100064351974958"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook TOTUPOINT"
                                title="Facebook"
                                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 ${fbBg}`}
                            >
                                <svg className="w-6 h-6" role="img" aria-hidden="true" viewBox="0 0 24 24">
                                    <path
                                        d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.35C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.732 0 1.325-.593 1.325-1.325V1.325C24 .593 23.407 0 22.675 0z"
                                        fill={iconFill}
                                    />
                                </svg>
                            </a>
                            <a
                                href="https://www.linkedin.com/company/totupoint/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn TOTUPOINT"
                                title="LinkedIn"
                                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 ${liBg}`}
                            >
                                <svg className="w-6 h-6" role="img" aria-hidden="true" viewBox="0 0 24 24">
                                    <path
                                        d="M20.447 20.452h-3.554v-5.569c0-1.328-.026-3.037-1.852-3.037-1.853 0-2.136 1.447-2.136 2.943v5.663H9.351V9h3.414v1.561h.048c.476-.9 1.64-1.852 3.374-1.852 3.608 0 4.274 2.376 4.274 5.466v6.277zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.114 20.452H3.557V9h3.557v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.73v20.541C0 23.226.792 24 1.771 24h20.454C23.2 24 24 23.226 24 22.271V1.73C24 .774 23.2 0 22.225 0z"
                                        fill={iconFill}
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

function generateAdditionTask(): Task {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return {
        question: `${num1} + ${num2} = ?`,
        correctAnswer: (num1 + num2).toString(),
        userAnswer: '',
    };
}

function generateSubtractionTask(): Task {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return {
        question: `${num1} - ${num2} = ?`,
        correctAnswer: (num1 - num2).toString(),
        userAnswer: '',
    };
}

function generateMultiplicationTask(): Task {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return {
        question: `${num1} * ${num2} = ?`,
        correctAnswer: (num1 * num2).toString(),
        userAnswer: '',
    };
}